import { queryOptions, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { handleResponse } from '../api/helpers.ts'
import {
	type CreateWebhook,
	type CreateWebhookSecret,
	notificationManager,
	type UpdateWebhook,
	type UpdateWebhookSecret,
	type Webhook,
	type WebhookInvocation,
	type WebhookSecret,
} from '../api/notification-manager.ts'
import { dayjs } from '../dayjs.ts'
import { asQueryParam, type Impersonation, useImpersonation } from './impersonation.ts'

export const webhooksQuery = (impersonation: Impersonation | undefined) =>
	queryOptions({
		queryKey: ['WEBHOOKS', impersonation?.subscriptionId],
		refetchInterval: dayjs.duration(1, 'minute').asMilliseconds(),
		queryFn: () =>
			notificationManager
				.GET('/notification-manager/webhooks', { params: { query: asQueryParam(impersonation) } })
				.then(handleResponse)
				.catch(() => []),
	})

export const useWebhooks = () => useQuery(webhooksQuery(useImpersonation()))

export const useCreateWebhook = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (input: CreateWebhook) =>
			notificationManager.POST('/notification-manager/webhooks', {
				body: input,
				params: { query: asQueryParam(impersonation) },
			}),
		onSettled: () => queryClient.invalidateQueries(webhooksQuery(impersonation)),
	})
}

export const useUpdateWebhook = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ webhookId, ...input }: UpdateWebhook & { webhookId: Webhook['id'] }) =>
			notificationManager.PATCH('/notification-manager/webhooks/{webhookId}', { body: input, params: { path: { webhookId } } }),
		onSettled: () => queryClient.invalidateQueries(webhooksQuery(impersonation)),
	})
}

export const useDeleteWebhook = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (webhookId: Webhook['id']) =>
			notificationManager.DELETE('/notification-manager/webhooks/{webhookId}', { params: { path: { webhookId } } }),
		onSettled: () => queryClient.invalidateQueries(webhooksQuery(impersonation)),
	})
}

export const webhookSecretsQuery = (webhookId: Webhook['id'] | undefined) =>
	queryOptions({
		queryKey: ['WEBHOOK_SECRETS', webhookId],
		refetchInterval: dayjs.duration(1, 'minute').asMilliseconds(),
		queryFn: webhookId
			? () =>
					notificationManager
						.GET('/notification-manager/webhooks/{webhookId}/secrets', { params: { path: { webhookId } } })
						.then(handleResponse)
						.catch(() => [] as WebhookSecret[])
			: skipToken,
	})

export const useWebhookSecrets = (webhookId: Webhook['id'] | undefined) => useQuery(webhookSecretsQuery(webhookId))

export const useWebhookSecretValue = ({ webhookId, secretId }: { webhookId: Webhook['id']; secretId: WebhookSecret['id'] }) => {
	return useQuery({
		queryKey: ['WEBHOOK_SECRET_VALUE', webhookId, secretId],
		gcTime: 0,
		queryFn: () =>
			notificationManager
				.GET('/notification-manager/webhooks/{webhookId}/secrets/{secretId}/value', {
					params: { path: { webhookId, secretId } },
					parseAs: 'text',
				})
				.then(handleResponse)
				.catch(() => null),
	})
}

export const useCreateWebhookSecret = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ webhookId, ...input }: CreateWebhookSecret & { webhookId: Webhook['id'] }) =>
			notificationManager.POST('/notification-manager/webhooks/{webhookId}/secrets', { body: input, params: { path: { webhookId } } }),
		onSettled: (_data, _error, { webhookId }) => queryClient.invalidateQueries(webhookSecretsQuery(webhookId)),
	})
}

export const useUpdateWebhookSecret = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ webhookId, secretId, ...input }: UpdateWebhookSecret & { webhookId: Webhook['id']; secretId: WebhookSecret['id'] }) =>
			notificationManager.PATCH('/notification-manager/webhooks/{webhookId}/secrets/{secretId}', {
				body: input,
				params: { path: { webhookId, secretId } },
			}),
		onSettled: (_data, _error, { webhookId }) => queryClient.invalidateQueries(webhookSecretsQuery(webhookId)),
	})
}

export const useDeleteWebhookSecret = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ webhookId, secretId }: { webhookId: Webhook['id']; secretId: WebhookSecret['id'] }) =>
			notificationManager.DELETE('/notification-manager/webhooks/{webhookId}/secrets/{secretId}', {
				params: { path: { webhookId, secretId } },
			}),
		onSettled: (_data, _error, { webhookId }) => queryClient.invalidateQueries(webhookSecretsQuery(webhookId)),
	})
}

export const recentWebhookInvocationsQuery = (webhookId: Webhook['id'] | undefined) =>
	queryOptions({
		queryKey: ['WEBHOOK_INVOCATIONS', webhookId],
		refetchInterval: dayjs.duration(30, 'seconds').asMilliseconds(),
		queryFn: webhookId
			? () =>
					notificationManager
						.GET('/notification-manager/webhooks/{webhookId}/invocations/recent', { params: { path: { webhookId } } })
						.then(handleResponse)
						.catch(() => [])
			: skipToken,
	})
export const useRecentWebhookInvocations = (webhookId: Webhook['id'] | undefined) => {
	return useQuery(recentWebhookInvocationsQuery(webhookId))
}

export const useNotificationWebhookContent = (notificationId: string) => {
	return useQuery({
		queryKey: ['NOTIFICATION_WEBHOOK_CONTENT', notificationId],
		queryFn: () =>
			notificationManager
				.GET('/notification-manager/notifications/{id}', { params: { path: { id: notificationId } } })
				.then(handleResponse)
				.then(({ webhookContent }) => webhookContent)
				.catch(() => null),
	})
}

export const useWebhookHeartbeatState = (webhookId: Webhook['id'] | undefined) => {
	return useQuery({
		queryKey: ['WEBHOOK_HEARTBEAT_STATE', webhookId],
		refetchInterval: dayjs.duration(1, 'minute').asMilliseconds(),
		queryFn: webhookId
			? () =>
					notificationManager
						.GET('/notification-manager/webhooks/{webhookId}/heartbeat-state', { params: { path: { webhookId } } })
						.then(handleResponse)
						.catch(() => null)
			: skipToken,
	})
}

export const webhookInvocationId = (invocation: WebhookInvocation) =>
	`${invocation.notificationId}-${invocation.webhookId}-${invocation.attempt}`
